import styled from "@emotion/styled"
import {
  DARK_BLUE_COLOR,
  HEADER_Z_INDEX,
  LIGHT_GREY_COLOR,
  PX16,
  PX18,
  PX24,
  PX28,
  PX32,
  SLIGHTLY_LIGHTER_GREY_COLOR,
} from "theme/consts"

import { Value } from "@stringtale/react"
import Group from "ui/Group"
import Stack from "ui/Stack"
import Text from "ui/Text"
import Title from "ui/Title"
import Link from "next/link"
import { TbArrowUpRight } from "@react-icons/all-files/tb/TbArrowUpRight"
import { TbArrowLeft } from "@react-icons/all-files/tb/TbArrowLeft"
import { Dispatch } from "react"
import { SetStateAction } from "jotai"
import { SupportTabs } from "./SupportPopover"
import { poppins } from "theme/fonts"
import { SUPPORT_LINKS, StandardEase } from "app/core/consts"
import Button from "ui/Button"

const Icon = styled.img`
  width: 46px;
  position: fixed;
  bottom: 20px;
  right: 80px;
  height: 42px;
  background-color: transparent;
  filter: drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.4));
  z-index: ${HEADER_Z_INDEX};
`

const SupportButtonCircle = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background-color: #0073e6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 10px;
  flex-grow: 0;
  flex-shrink: 0;

  overflow: hidden;

  display: grid;
  place-items: center;

  .icon-1 {
    transition: transform 0.2s ease;
    grid-area: 1 / 1;
    transition: transform 0.2s ease;
    transition-timing-function: ${StandardEase};
  }
  .icon-2 {
    transform: translate(-110%, 110%);
    transition: transform 0.2s ease;
    grid-area: 1 / 1;
    transition-timing-function: ${StandardEase};
  }
`

const SupportButtonIconContainer = styled.div<{ backgroundColor?: string }>`
  width: 52px;
  height: 52px;

  background-color: ${(p) =>
    p.backgroundColor ? p.backgroundColor : "transparent"};

  display: flex;
  align-items: center;
  justify-content: center;
  flex-grow: 0;
  flex-shrink: 0;
  overflow: visible;
`

const SupportButtonLink = styled(Link)`
  text-decoration: none;
  background-color: white;
  border-radius: 24px;
  width: calc(100% - 20px);
  transition: background-color 0.2s ease;
  overflow: hidden;
  cursor: pointer;

  &:hover {
    background-color: ${SLIGHTLY_LIGHTER_GREY_COLOR};
    ${SupportButtonIconContainer} img {
      transform: scale(1.15);
    }
    ${SupportButtonCircle} {
      .icon-1 {
        transform: translate(110%, -110%);
      }
      .icon-2 {
        transform: translate(0, 0);
      }
    }
  }

  ${SupportButtonIconContainer} img {
    transition: transform 0.15s ease;
    transition-timing-function: ${StandardEase};
  }
`

const SupporButtonDivider = styled.div`
  height: 1px;
  width: calc(100% - 40px);

  background-color: #e0e0e0;
  box-sizing: border-box;
  margin: 6px 0;
`

const SupportBackButton = styled(Button)`
  width: 40px;
  height: 40px;
  background-color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 18px;
  left: 18px;
  overflow: hidden;

  display: grid;
  place-items: center;

  .icon-1 {
    transition: transform 0.2s ease;
    grid-area: 1 / 1;
    transition: transform 0.2s ease;
    transition-timing-function: ${StandardEase};
  }
  .icon-2 {
    transform: translateX(110%);
    transition: transform 0.2s ease;
    grid-area: 1 / 1;
    transition-timing-function: ${StandardEase};
  }

  &:hover {
    background-color: ${SLIGHTLY_LIGHTER_GREY_COLOR};
  }
  &:hover .icon-1 {
    transform: translateX(-110%);
  }
  &:hover .icon-2 {
    transform: translateX(0);
  }
`

const SupportButtonText = styled(Text)`
  font-family: ${poppins.style.fontFamily};
  line-height: ${PX18};
`

const SupportButtonContentContainer = styled(Group)`
  height: 64px;
  width: 100%;
  padding: 6px 12px 6px 6px;
  box-sizing: border-box;
`

function SupportButton({
  icon,
  label,
  link,
  backgroundColor,
}: {
  icon: string
  label: React.ReactNode
  link: string
  backgroundColor?: string
}) {
  return (
    <SupportButtonLink href={link} target="_blank" rel="noopener noreferrer">
      <SupportButtonContentContainer align="center" justify="space-between">
        <Group gap="10px" justify="flex-start" align="center">
          <SupportButtonIconContainer backgroundColor={backgroundColor}>
            <img src={icon} height={46} width={46} />
          </SupportButtonIconContainer>
          <SupportButtonText size={PX16} bold>
            {label}
          </SupportButtonText>
        </Group>
        {/* <img
        src={"/icons/supp_icon_bl.svg"}
        height={20}
        width={20}
        style={{ cursor: "pointer" }}
      /> */}

        <SupportButtonCircle>
          <TbArrowUpRight size={24} color="white" className="icon-1" />
          <TbArrowUpRight size={24} color="white" className="icon-2" />
        </SupportButtonCircle>
      </SupportButtonContentContainer>
    </SupportButtonLink>
  )
}

const SupportContentTitle = styled(Title)`
  margin-top: 50px;
  font-family: ${poppins.style.fontFamily};
  line-height: ${PX32};
`

const SupportContentContainer = styled(Stack)`
  height: 100%;
`

const SupportContentBackground = styled(Stack)`
  background-color: #3baffa;
  flex: 1;
  border-radius: 30px 30px 0 0;
`

const SupportContentImage = styled.img`
  margin-top: -60px;
  z-index: 1;
`

const SupportContentDrawer = styled(Stack)`
  background-color: white;
  margin-top: -36px;
  border-radius: 36px 36px 30px 30px;
  overflow: hidden;
  padding: 10px 0 16px 0;
`

export default function StaticSupportContent({
  setCurrentTab,
}: {
  setCurrentTab: Dispatch<SetStateAction<SupportTabs>>
}) {
  return (
    <SupportContentContainer align="center" justify="space-between">
      <SupportContentBackground align="center" justify="flex-start">
        <SupportBackButton
          aria-label="Terug naar de vorige pagina"
          onClick={() => setCurrentTab("supportContent")}
        >
          <TbArrowLeft size={28} color={DARK_BLUE_COLOR} className="icon-1" />
          <TbArrowLeft size={28} color={DARK_BLUE_COLOR} className="icon-2" />
        </SupportBackButton>
        <SupportContentImage src={"/icons/support/support.png"} width={180} />
        <SupportContentTitle size={PX28} weight="700" color="white" center>
          <Value
            name="apps.web.src.core.components.supportbutton.help_me_plz"
            format={{ br: <br /> }}
          >
            {`Ondersteuning{br}bij 123ZING`}
          </Value>
        </SupportContentTitle>
      </SupportContentBackground>
      <SupportContentDrawer align="center">
        <SupportButton
          icon={"/icons/support/questionmarks.png"}
          label={
            <Value name="apps.web.src.support.components.staticsupportcontent.hulp_aan_de_slag">
              {`Hulp & Aan de slag`}
            </Value>
          }
          link={SUPPORT_LINKS["GENERAL_SUPPORT"]}
        />

        <SupporButtonDivider />

        <SupportButton
          icon={"/icons/support/nosignal.png"}
          label={
            <Value name="apps.web.src.support.components.staticsupportcontent.support_bij_video_problemen">
              {`Problemen met video's afspelen`}
            </Value>
          }
          link={SUPPORT_LINKS["SUPPORT_VIDEOS"]}
        />

        <SupporButtonDivider />

        <SupportButton
          icon={"/icons/support/phone.png"}
          label={
            <Value name="apps.web.src.support.components.staticsupportcontent.contact_met_123zing">
              {`Contact met klantenservice`}
            </Value>
          }
          link={SUPPORT_LINKS["CONTACT_SUPPORT"]}
        />
      </SupportContentDrawer>
    </SupportContentContainer>
  )
}
